import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { trpc } from "@/lib/trpc";
import { AgencyLogo } from "@/components/AgencyLogo";
import { BarometerNav } from "@/components/BarometerNav";
import { BarometerHero } from "@/components/BarometerHero";
import { CrossSelling } from "@/components/CrossSelling";
import { generateAccessibilityPdf } from "@/lib/generatePdf";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";

// ─── Konstanten ───────────────────────────────────────────────────────────────
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

const C = {
  indigo: "#3730a3",
  indigoDark: "#2e2780",
  indigoLight: "#e0e7ff",
  text: "#111827",
  muted: "#6b7280",
  bgGray: "#f9fafb",
};

// ─── Typen ────────────────────────────────────────────────────────────────────
interface DimDetail {
  check: string;
  status: "ok" | "fehlt" | "warnung" | "kritisch" | "info";
  punkte: number;
  hinweis?: string;
}
interface Dimension {
  dimension: string;
  punkte: number;
  max: number;
  details: DimDetail[];
}
interface Optimierung {
  dimension: string;
  check: string;
  hinweis: string;
  prioritaet: "kritisch" | "hoch" | "mittel" | "niedrig";
}
interface AnalyseResult {
  url: string;
  domain: string;
  total_score: number;
  max_score: number;
  prozent: number;
  grade: string;
  analyse_dauer: number;
  dimensionen: Dimension[];
  optimierungen: Optimierung[];
  wcag_level?: string;
  has_aria?: boolean;
  has_alt_texts?: boolean;
  keyboard_navigable?: boolean;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────
function gradeColor(grade: string): string {
  if (grade.startsWith("A")) return "#059669";
  if (grade === "B") return "#0284c7";
  if (grade === "C") return C.indigo;
  if (grade === "D") return "#d97706";
  return "#dc2626";
}

function statusIcon(status: string): string {
  if (status === "ok") return "✓";
  if (status === "kritisch") return "✗";
  if (status === "warnung") return "⚠";
  return "–";
}

function statusColor(status: string): string {
  if (status === "ok") return "#059669";
  if (status === "kritisch") return "#dc2626";
  if (status === "warnung") return "#d97706";
  return C.muted;
}

// ─── Ergebnisseite ────────────────────────────────────────────────────────────
function AccessibilityResultsView({ data, onReset }: { data: AnalyseResult; onReset: () => void }) {
  const [activeTab, setActiveTab] = useState<"overview" | "dimensions" | "recommendations">("overview");

  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (data.prozent / 100) * circumference;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/accessibility-barometer" accentColor={C.indigo} />
      <div style={{ height: "4px", backgroundColor: C.indigo }} />

      {/* Ergebnis-Hero */}
      <section className="bg-black px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Score-Ring */}
            <div className="flex-shrink-0 relative" style={{ width: 140, height: 140 }}>
              <svg width="140" height="140" viewBox="0 0 140 140" style={{ position: "absolute", top: 0, left: 0 }}>
                <circle cx="70" cy="70" r="54" fill="none" stroke="#1f2937" strokeWidth="12" />
                <circle cx="70" cy="70" r="54" fill="none" stroke={gradeColor(data.grade)} strokeWidth="12"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  strokeLinecap="round" transform="rotate(-90 70 70)" />
              </svg>
              <div style={{ position: "absolute", top: 0, left: 0, width: 140, height: 140, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "28px", fontWeight: 900, color: "white", lineHeight: 1 }}>{data.prozent}</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "13px", color: "#9ca3af", lineHeight: 1.4 }}>von 100</span>
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.indigo }}>Accessibility-Barometer™ Ergebnis</p>
              <h1 className="font-black uppercase text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3rem)" }}>{data.domain}</h1>
              <p className="text-gray-400 text-sm mb-4">{data.total_score} von {data.max_score} Punkten · Analyse in {data.analyse_dauer}s</p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                {data.has_aria && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: "#059669", borderColor: "#059669" }}>ARIA vorhanden</span>}
                {data.has_alt_texts && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: "#059669", borderColor: "#059669" }}>Alt-Texte erkannt</span>}
                {data.keyboard_navigable && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: "#059669", borderColor: "#059669" }}>Tastaturnavigation</span>}
                {data.wcag_level && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: C.indigo, borderColor: C.indigo }}>WCAG {data.wcag_level}</span>}
              </div>
            </div>
            <button onClick={onReset} className="text-xs font-bold uppercase tracking-widest px-6 py-3 border border-gray-600 text-gray-300 hover:border-white hover:text-white transition-colors">
              Neue Analyse
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex">
          {(["overview", "dimensions", "recommendations"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-6 py-4 text-xs font-bold uppercase tracking-widest transition-colors border-b-2"
              style={{
                borderColor: activeTab === tab ? C.indigo : "transparent",
                color: activeTab === tab ? C.indigo : C.muted,
              }}>
              {tab === "overview" ? "Übersicht" : tab === "dimensions" ? "Dimensionen" : "Empfehlungen"}
            </button>
          ))}
        </div>
      </div>

      {/* Tab-Inhalt */}
      <div className="max-w-4xl mx-auto w-full px-6 py-10">
        {activeTab === "overview" && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.dimensionen.map((dim) => (
                <div key={dim.dimension} className="p-4 border border-gray-200 text-center">
                  <div className="font-black text-2xl mb-1" style={{ color: C.indigo, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.punkte}/{dim.max}</div>
                  <div className="text-xs text-gray-500 leading-tight">{dim.dimension}</div>
                  <div className="mt-2 h-1 bg-gray-100">
                    <div className="h-1 transition-all" style={{ width: `${(dim.punkte / dim.max) * 100}%`, backgroundColor: C.indigo }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-gray-200 p-6">
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: C.indigo }}>Wichtigste Maßnahmen</p>
              <div className="space-y-3">
                {data.optimierungen.filter(o => o.prioritaet === "kritisch" || o.prioritaet === "hoch").slice(0, 5).map((opt, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-xs font-bold px-2 py-0.5 flex-shrink-0" style={{
                      backgroundColor: opt.prioritaet === "kritisch" ? "#fef2f2" : "#fffbeb",
                      color: opt.prioritaet === "kritisch" ? "#dc2626" : "#d97706",
                    }}>{opt.prioritaet.toUpperCase()}</span>
                    <p className="text-sm text-gray-700">{opt.hinweis}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "dimensions" && (
          <div className="space-y-6">
            {data.dimensionen.map((dim) => (
              <div key={dim.dimension} className="border border-gray-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <h3 className="font-bold text-sm uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.dimension}</h3>
                  <span className="font-black text-lg" style={{ color: C.indigo, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.punkte}/{dim.max}</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {dim.details.map((detail, i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-3">
                      <span className="font-bold text-sm flex-shrink-0 w-5 text-center" style={{ color: statusColor(detail.status) }}>{statusIcon(detail.status)}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{detail.check}</p>
                        {detail.hinweis && <p className="text-xs text-gray-500 mt-0.5">{detail.hinweis}</p>}
                      </div>
                      <span className="text-xs font-bold flex-shrink-0" style={{ color: C.indigo }}>{detail.punkte}P</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "recommendations" && (
          <div className="space-y-4">
            {data.optimierungen.map((opt, i) => (
              <div key={i} className="border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="font-bold text-sm">{opt.check}</h4>
                  <span className="text-xs font-bold px-2 py-0.5 flex-shrink-0" style={{
                    backgroundColor: opt.prioritaet === "kritisch" ? "#fef2f2" : opt.prioritaet === "hoch" ? "#fffbeb" : "#f0fdf4",
                    color: opt.prioritaet === "kritisch" ? "#dc2626" : opt.prioritaet === "hoch" ? "#d97706" : "#059669",
                  }}>{opt.prioritaet.toUpperCase()}</span>
                </div>
                <p className="text-xs text-gray-500 mb-1">{opt.dimension}</p>
                <p className="text-sm text-gray-700">{opt.hinweis}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="border-t border-gray-200 py-12 px-6 text-center">
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.indigo }}>Professionelle Beratung</p>
        <h2 className="font-black uppercase mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>Barrierefreiheit umsetzen</h2>
        <p className="text-gray-500 text-sm max-w-lg mx-auto mb-6">Barrierefreiheit ist ab 28.06.2025 für viele Unternehmen gesetzlich vorgeschrieben. Jetzt ist der richtige Zeitpunkt, den Status deiner Website zu kennen.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => generateAccessibilityPdf(data as unknown as Record<string, unknown>)}
            className="text-white font-bold text-sm uppercase tracking-widest px-8 py-4 transition-colors"
            style={{ backgroundColor: C.indigo, fontFamily: "'DM Sans', sans-serif" }}>
            📄 PDF-Report herunterladen
          </button>
          <a href="https://www.werbeagentur-larissa-girenko.de/kontakt" target="_blank" rel="noopener noreferrer"
            className="inline-block text-white font-bold text-sm tracking-widest uppercase px-10 py-4"
            style={{ backgroundColor: C.indigo }}>Beratungsgespräch anfragen →</a>
        </div>
      </section>

      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-center">
            {["/", "/sensorik", "/lokal", "/geo-barometer", "/seo-barometer", "/conversion-barometer", "/accessibility-barometer", "/datenschutz-barometer", "/impressum", "/datenschutz"].map((href) => (
              <a key={href} href={href} className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {href === "/" ? "Übersicht" : href.replace("/", "").replace("-barometer", "").replace("-", " ")}
              </a>
            ))}
          </div>
          <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}

// ─── Ladeanimation ────────────────────────────────────────────────────────────
function LoadingScreen({ url }: { url: string }) {
  const steps = ["Seite laden…", "WCAG 2.2 prüfen…", "ARIA-Rollen analysieren…", "Tastaturnavigation testen…", "Alt-Texte prüfen…", "Farbkontraste messen…", "Formular-Zugänglichkeit…", "Ergebnis berechnen…"];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setStep(s => Math.min(s + 1, steps.length - 1)), 4000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/accessibility-barometer" accentColor={C.indigo} />
      <div className="text-center mt-16">
        <div className="w-16 h-16 border-4 border-gray-700 border-t-indigo-600 rounded-full animate-spin mx-auto mb-8" style={{ borderTopColor: C.indigo }} />
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.indigo }}>Accessibility-Barometer™ analysiert</p>
        <h2 className="font-black uppercase text-white mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>{url}</h2>
        <p className="text-gray-400 text-sm mb-8">{steps[step]}</p>
        <div className="flex gap-2 justify-center">
          {steps.map((_, i) => (
            <div key={i} className="w-2 h-2 rounded-full transition-all" style={{ backgroundColor: i <= step ? C.indigo : "#374151" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function AccessibilityHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "Accessibility-Barometer™ – Barrierefreiheit & WCAG-Analyse | WERBEAGENTUR Larissa Girenko",
    description: "BFSG ab 2025: Ist deine Website barrierefrei? Accessibility-Barometer™ prüft WCAG 2.1, Tastaturnavigation, Kontraste. Vollanalyse ab 97 €.",
    path: "/accessibility-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<AnalyseResult | null>(null);
  const [previewResult, setPreviewResult] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analyzedUrl, setAnalyzedUrl] = useState("");

  const analyze = trpc.accessibilityBarometer.analyze.useMutation({
    onSuccess: (data) => {
      setIsLoading(false);
      if (data.success && data.data) {
        const raw = data.data as Record<string, unknown>;
        // Server gibt 'scores' zurück, Frontend erwartet 'dimensionen'
        const scores = (raw.scores ?? {}) as Record<string, { label?: string; score?: number; max?: number; details?: Record<string, { score?: number; max?: number; found?: boolean }> }>;
        const dimensionen = Object.entries(scores).map(([, dim]) => ({
          dimension: String(dim.label ?? ''),
          punkte: Number(dim.score ?? 0),
          max: Number(dim.max ?? 25),
          details: Object.entries(dim.details ?? {}).map(([key, d]) => ({
            check: key.replace(/_/g, ' '),
            status: (d.found === false ? 'fehlt' : d.score === 0 ? 'kritisch' : d.score && d.max && d.score >= d.max * 0.7 ? 'ok' : 'warnung') as 'ok' | 'fehlt' | 'warnung' | 'kritisch' | 'info',
            punkte: Number(d.score ?? 0),
            hinweis: undefined,
          })),
        }));
        const totalScore = Number(raw.total_score ?? 0);
        const maxScore = 100;
        const prozent = Math.round((totalScore / maxScore) * 100);
        const grade = prozent >= 90 ? 'A+' : prozent >= 80 ? 'A' : prozent >= 70 ? 'B' : prozent >= 60 ? 'C' : prozent >= 50 ? 'D' : 'F';
        // Optimierungen mappen: 'kriterium'/'titel' → 'check', 'beschreibung' → 'hinweis'
        const optimierungen = ((raw.optimierungen ?? []) as Array<Record<string, unknown>>).map((o) => ({
          dimension: String(o.dimension ?? ''),
          check: String(o.titel ?? o.kriterium ?? o.check ?? ''),
          hinweis: String(o.beschreibung ?? o.hinweis ?? ''),
          prioritaet: (o.prioritaet === 'kritisch' ? 'kritisch' : o.prioritaet === 'hoch' ? 'hoch' : o.prioritaet === 'niedrig' ? 'niedrig' : 'mittel') as 'kritisch' | 'hoch' | 'mittel' | 'niedrig',
        }));
        const mapped: AnalyseResult = {
          url: String(raw.url ?? ''),
          domain: String(raw.domain ?? ''),
          total_score: totalScore,
          max_score: maxScore,
          prozent,
          grade,
          analyse_dauer: Number(raw.analyse_dauer ?? 0),
          dimensionen,
          optimierungen,
          wcag_level: raw.wcag_level as string | undefined,
          has_aria: Boolean(raw.has_aria),
          has_alt_texts: Boolean(raw.has_alt_texts),
          keyboard_navigable: Boolean(raw.keyboard_navigable),
        };
        setResult(mapped);
      }
    },
    onError: () => setIsLoading(false),
  });
  const previewMutation = trpc.accessibilityBarometer.analyzePreview.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) setPreviewResult(res.data as Record<string, unknown>);
    },
    onError: (err) => { setIsLoading(false); console.error(err.message); },
  });

  const handleAnalyze = () => {
    if (!url.trim()) return;
    setAnalyzedUrl(url.trim());
    setResult(null);
    setPreviewResult(null);
    setIsLoading(true);
    if (isAdminUser) {
      analyze.mutate({ url: url.trim() });
    } else {
      previewMutation.mutate({ url: url.trim() });
    }
  };
  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank");
    },
    onError: (err) => toast.error(err.message),
  });
  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "accessibility_analyse",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/accessibility-barometer",
    });
  };
  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "accessibility_mit_videocall",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/accessibility-barometer",
    });
  };
  const handleReset = () => { setResult(null); setPreviewResult(null); setUrl(""); analyze.reset(); previewMutation.reset(); };
  const handleBooking = () => {
    const bookingUrl = url.trim()
      ? `${MEETERGO_URL}&notes=${encodeURIComponent("Website: " + url.trim())}`
      : MEETERGO_URL;
    window.open(bookingUrl, "_blank", "noopener,noreferrer");
  };
  if (isLoading) return <LoadingScreen url={analyzedUrl} />;
  if (result) return <AccessibilityResultsView data={result} onReset={handleReset} />;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/accessibility-barometer" accentColor={C.indigo} />
      <div style={{ height: "4px", backgroundColor: C.indigo }} />

      <BarometerHero
        name="Accessibility-Barometer™"
        headline="EAA ab 28.06.2025 Pflicht —<br />ist deine Website WCAG 2.2-konform?"
        subtext={`8 Dimensionen · 160+ Kriterien · WCAG 2.2, ARIA, EAA. Kostenlose Vorschau in 60 Sekunden — persönliche Vollanalyse von Larissa ab ${formatPrice(BAROMETER_PRICES.accessibility)}.`}
        accentColor={C.indigo}
        ctaColor="#3730a3"
        stats={[
          { val: "8", label: "Dimensionen" },
          { val: "160+", label: "Kriterien" },
          { val: "WCAG 2.2", label: "Standard" },
        ]}
        url={url}
        onUrlChange={setUrl}
        onSubmit={handleAnalyze}
        isLoading={isLoading}
        error=""
        isAdmin={isAdminUser}
        adminCtaText="Accessibility-Vollanalyse starten →"
      />

      {/* Preview-Ergebnis: nur Scores */}
      {previewResult && (
        <section className="max-w-3xl mx-auto w-full px-6 py-12">
          <div className="border border-gray-200 p-6" style={{ backgroundColor: C.bgGray }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: C.indigo }} />
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: C.indigo }}>Accessibility-Barometer™ Vorschau</p>
              <span className="text-xs text-gray-400 truncate">{url}</span>
            </div>
            {(() => {
              const score = Number(previewResult.total_score ?? 0);
              const maxScore = Number(previewResult.max_score ?? 100);
              const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
              const grade = pct >= 80 ? "A" : pct >= 65 ? "B" : pct >= 45 ? "C" : pct >= 25 ? "D" : "F";
              const gColor = pct >= 70 ? "#16a34a" : pct >= 45 ? "#ca8a04" : "#dc2626";
              return (
                <div className="flex items-center gap-6 mb-6 p-4 bg-white border border-gray-200">
                  <div className="text-center flex-shrink-0">
                    <div className="text-5xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>{pct}</div>
                    <div className="text-xs text-gray-400">von 100</div>
                  </div>
                  <div>
                    <div className="text-lg font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>Note {grade} · {pct >= 70 ? "WCAG-konform" : pct >= 45 ? "Teilweise konform" : "Kritisch"}</div>
                    <div className="text-sm text-gray-500 mt-1">{score} von {maxScore} Punkten</div>
                  </div>
                </div>
              );
            })()}
            {(() => {
              const dims = (previewResult.dimension_scores as Array<{ label: string; score: number; max: number }>) ?? [];
              return dims.length > 0 ? (
                <div className="space-y-2 mb-6">
                  <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.indigo }}>Dimensionen auf einen Blick</p>
                  {dims.map((d) => {
                    const dp = d.max > 0 ? Math.round((d.score / d.max) * 100) : 0;
                    const dc = dp >= 70 ? "#16a34a" : dp >= 45 ? "#ca8a04" : "#dc2626";
                    return (
                      <div key={d.label} className="flex items-center gap-3">
                        <span className="text-xs w-40 flex-shrink-0">{d.label}</span>
                        <div className="flex-1 bg-gray-200 h-2"><div style={{ width: `${dp}%`, backgroundColor: dc, height: "100%" }} /></div>
                        <span className="text-xs font-bold w-8 text-right" style={{ color: dc }}>{dp}%</span>
                      </div>
                    );
                  })}
                </div>
              ) : null;
            })()}
            <div className="border-t border-gray-200 pt-5">
              <p className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt — Wähle deine Option</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="border-2 border-gray-200 p-5 flex flex-col">
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
                  <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.indigo }}>{formatPrice(BAROMETER_PRICES.accessibility)}</div>
                  <button onClick={handleBuy} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: C.indigo, fontFamily: "'DM Sans', sans-serif" }}>Analyse buchen</button>
                </div>
                <div className="border-2 p-5 flex flex-col relative" style={{ borderColor: "#6d0000", backgroundColor: "#6d000008" }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                  <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                  <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
                  <button onClick={handleBuyVideoCall} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Analyse + VideoCall buchen</button>
                </div>
              </div>
              <button onClick={handleReset} className="block w-full text-center text-xs text-gray-400 mt-2 hover:text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Andere URL analysieren</button>
            </div>
          </div>
        </section>
      )}


      {/* Dimensionen-Kacheln */}
      <section className="border-t border-gray-200 py-16 px-6" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { icon: "/manus-storage/16_9c3f7718.svg", label: "Tastaturnavigation", desc: "Tab-Reihenfolge, Focus-Sichtbarkeit" },
              { icon: "/manus-storage/11_094992a3.svg", label: "Alt-Texte & Bilder", desc: "Bildbeschreibungen, dekorative Bilder" },
              { icon: "/manus-storage/11_094992a3.svg", label: "Farbkontraste", desc: "WCAG AA/AAA Kontrastwerte" },
              { icon: "/manus-storage/32_04885248.svg", label: "Formulare", desc: "Labels, Fehlermeldungen, ARIA" },
              { icon: "/manus-storage/16_9c3f7718.svg", label: "Textstruktur", desc: "Überschriften-Hierarchie, Lesbarkeit" },
              { icon: "/manus-storage/33_d51b7819.svg", label: "Responsive", desc: "Zoom, Viewport, Touch-Ziele" },
              { icon: "/manus-storage/11_094992a3.svg", label: "Screen-Reader", desc: "ARIA-Rollen, Landmarks, Live-Regions" },
              { icon: "/manus-storage/29_7f3bb394.svg", label: "EAA-Compliance", desc: "European Accessibility Act 2025" },
            ].map((f) => (
              <div key={f.label} className="border p-4 text-center bg-white">
                <img src={f.icon} alt="" className="w-7 h-7 mb-2 object-contain" style={{ opacity: 0.85 }} />
                <div className="text-xs font-bold uppercase tracking-widest mb-1">{f.label}</div>
                <div className="text-xs" style={{ color: C.muted }}>{f.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.indigo }}>Warum Barrierefreiheit entscheidend ist</p>
            <h3 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>1 von 5 Menschen<br />hat eine Beeinträchtigung</h3>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto">Der European Accessibility Act (EAA) ist seit 28.06.2025 in Kraft. Viele Unternehmen sind zur WCAG 2.1 AA-Konformität verpflichtet – bei Verstößen drohen Bußgelder und Abmahnungen.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { number: "01", title: "URL eingeben", desc: "Gib deine Website-URL ein – keine Anmeldung erforderlich." },
              { number: "02", title: "WCAG-Analyse", desc: "160+ Accessibility-Kriterien nach WCAG 2.2 werden automatisch in unter 60 Sekunden geprüft." },
              { number: "03", title: "Bericht erhalten", desc: "Du bekommst einen detaillierten Report mit priorisierten Handlungsempfehlungen." },
            ].map((v) => (
              <div key={v.number} className="p-6 border" style={{ borderColor: "#e5e7eb" }}>
                <span className="font-black text-4xl" style={{ color: C.indigo, fontFamily: "'Bebas Neue', sans-serif" }}>{v.number}</span>
                <h4 className="font-bold text-sm uppercase tracking-widest mt-2 mb-2">{v.title}</h4>
                <p className="text-xs text-gray-500">{v.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { stat: "20%", label: "der Bevölkerung", sub: "hat eine dauerhafte Beeinträchtigung (WHO)" },
              { stat: "71%", label: "behinderter Nutzer", sub: "verlassen Websites wegen Barrieren (WebAIM)" },
              { stat: "98%", label: "der Top-Websites", sub: "haben WCAG-Fehler (WebAIM Million 2024)" },
            ].map((s) => (
              <div key={s.stat} className="p-6 text-center border" style={{ borderColor: "#e5e7eb" }}>
                <div className="font-black text-4xl mb-2" style={{ color: C.indigo, fontFamily: "'Bebas Neue', sans-serif" }}>{s.stat}</div>
                <div className="text-sm mb-1">{s.label}</div>
                <div className="text-xs" style={{ color: C.muted }}>{s.sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 border-l-4 pl-6 py-2 max-w-2xl mx-auto" style={{ borderColor: C.indigo }}>
            <p className="text-sm italic text-gray-600 mb-2">"Barrierefreiheit ist kein Nice-to-have – sie ist ein Menschenrecht und ab 2025 für viele Unternehmen gesetzlich vorgeschrieben. Der Accessibility-Barometer macht messbar, wo deine Website steht."</p>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.indigo }}>– Larissa Girenko</p>
          </div>

          <div className="mt-12 text-center">
            <button onClick={handleBooking}
              className="text-white font-bold text-sm tracking-widest uppercase px-10 py-4 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#115751" }}
            >Jetzt kostenlose Erstberatung buchen</button>
          </div>
        </div>
      </section>

      {/* Larissa-Sektion */}
      <section className="border-t border-gray-200 py-16 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
                <img src={PORTRAIT_IMG} alt="Larissa Girenko – Accessibility-Expertin" className="w-full object-cover object-top" style={{ maxHeight: "480px" }} />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.indigo }}>Gegründet & entwickelt von</p>
              <h2 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>Larissa Girenko</h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                Barrierefreiheit wird zu oft als lästige Pflicht behandelt – dabei ist sie eine riesige Chance. Wer seine Website zugänglich macht, erreicht mehr Menschen und schützt sich vor rechtlichen Risiken.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Basierend auf WCAG 2.2, dem European Accessibility Act und den neuesten WebAIM-Studien – das erste messbare System für Website-Barrierefreiheit, das auch kleine Unternehmen ohne Agentur nutzen können.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[{ value: "8", label: "Dimensionen" }, { value: "160+", label: "Kriterien geprüft" }, { value: "WCAG 2.2", label: "Aktueller Standard" }, { value: "EAA", label: "EU-Compliance" }].map((stat) => (
                  <div key={stat.label} className="border-l-2 pl-4" style={{ borderColor: C.indigo }}>
                    <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{stat.value}</p>
                    <p className="text-gray-400 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Selling */}
      <CrossSelling
        headline="Accessibility gecheckt – jetzt auch auffindbar werden"
        subline="Barrierefreiheit ist die Basis – diese Barometer sichern deinen Erfolg in allen Dimensionen."
        items={[
          {
            icon: "/manus-storage/29_7f3bb394.svg",
            title: "Datenschutz-Barometer™",
            description: "DSGVO-Konformität, Cookie-Banner, Impressum, SSL – rechtlich und technisch sicher.",
            price: formatPrice(BAROMETER_PRICES.datenschutz),
            href: "/datenschutz-barometer",
            accent: "#3730a3",
            badge: "Ergänzt Accessibility",
          },
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "Barrierefreiheit verbessert SEO. Prüfe jetzt auch deine 100+ SEO-Kriterien.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
          },
          {
            icon: "/manus-storage/11_094992a3.svg",
            title: "Sensorik-Barometer™",
            description: "Wie wirkt deine Website auf das Unterbewusstsein? Sensorik + Accessibility = maximale Wirkung.",
            price: formatPrice(BAROMETER_PRICES.sensorik),
            href: "/sensorik",
            accent: "#6d0000",
          },
        ]}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-center">
            {[{ href: "/", label: "Übersicht" }, { href: "/sensorik", label: "Sensorik" }, { href: "/lokal", label: "Lokal" }, { href: "/geo-barometer", label: "GEO" }, { href: "/seo-barometer", label: "SEO" }, { href: "/conversion-barometer", label: "Conversion" }, { href: "/accessibility-barometer", label: "Accessibility" }, { href: "/datenschutz-barometer", label: "Datenschutz" }, { href: "/impressum", label: "Impressum" }, { href: "/datenschutz", label: "Datenschutz" }].map(({ href, label }) => (
              <a key={href} href={href} className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase">{label}</a>
            ))}
          </div>
          <p className="text-xs text-gray-400">© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}
